import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      // Redirect new users to onboarding
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Panel - Branding */}
      <div className="auth-left hidden md:flex relative bg-slate-900 w-1/2 items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1760239037245-a372db8630f3?crop=entropy&cs=srgb&fm=jpg&q=85)'
          }}
        />
        <div className="relative z-10 text-center px-12">
          <div className="w-20 h-20 bg-blue-600 rounded-sm mx-auto mb-8 flex items-center justify-center">
            <Shield size={40} className="text-white" />
          </div>
          <h2 className="font-secondary font-bold text-4xl text-white mb-4">
            Zentry
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Enterprise-grade Governance, Risk, and Compliance platform. 
            Map once, comply with many frameworks.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 text-center">
            <div className="w-16 h-16 bg-slate-900 rounded-sm mx-auto mb-4 flex items-center justify-center">
              <Shield size={32} className="text-white" />
            </div>
            <h1 className="font-secondary font-bold text-2xl text-slate-900">GRC Platform</h1>
          </div>

          <h1 className="font-secondary font-bold text-3xl text-slate-900 mb-2">
            Create your account
          </h1>
          <p className="text-slate-500 mb-8">
            Start managing your compliance in minutes
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <Input
                id="name"
                type="text"
                data-testid="register-name-input"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1.5 h-11 rounded-sm"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 h-11 rounded-sm"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="register-password-input"
                placeholder="Min. 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 h-11 rounded-sm"
              />
            </div>

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-sm font-medium"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create Account
                  <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-500">
            Already have an account?{' '}
            <Link 
              to="/login" 
              data-testid="login-link"
              className="text-blue-600 font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
